import React, { useRef, useState, useEffect } from "react";
import {
  ALLOWED_EXTENSIONS_DISPLAY,
  MAX_FILE_SIZE_MB,
  MAX_FILE_SIZE_BYTES,
  isFileTypeAllowed,
  formatFileSize,
} from "../utils/fileUploadUtils";
import { useAppDispatch, useAppSelector } from "./reduxHooks";
import { sendMessage, updateUploadInTable } from "../ChatSlice_Store";
import { selectCurrentProjectId } from "../ProjectSlice_Store";
import { uploadFileToProject } from "../services/fileUploadService";
import { getCurrentUserId } from "../utils/currentUser";
import type { AppDispatch } from "../types/redux";

const FILE_TYPE_ERROR_TIMEOUT_MS = 5000;

export interface FileUploads {
  uploadStatus: string;
  uploadError: string;
  uploadingFile: boolean;
  fileTypeError: string;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  clearFileTypeError: () => void;
  openFilePicker: () => void;
  handleFileChange: (event: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
}

/** Report the first blocking validation problem across the selected files. */
function validationError(files: File[]): string | null {
  const invalidTypeFiles = files.filter((file) => !isFileTypeAllowed(file.name));
  if (invalidTypeFiles.length > 0) {
    const invalidNames = invalidTypeFiles.map((f) => f.name).join(", ");
    return `Unsupported file type: ${invalidNames}. Allowed formats: ${ALLOWED_EXTENSIONS_DISPLAY}`;
  }

  const oversizedFiles = files.filter((file) => file.size > MAX_FILE_SIZE_BYTES);
  if (oversizedFiles.length === 1) {
    const file = oversizedFiles[0];
    return `"${file.name}" (${formatFileSize(file.size)}) exceeds the ${MAX_FILE_SIZE_MB} MB limit. Please reduce the file size or split into smaller documents.`;
  }
  if (oversizedFiles.length > 1) {
    const fileList = oversizedFiles
      .map((f) => `• ${f.name} (${formatFileSize(f.size)})`)
      .join("\n");
    return `${oversizedFiles.length} files exceed the ${MAX_FILE_SIZE_MB} MB limit:\n${fileList}`;
  }

  return null;
}

/** Mirror the upload service's phase changes into the uploads table. */
function statusReporter(
  dispatch: AppDispatch,
  uploadId: string,
  setUploadStatus: (status: string) => void
): (status: string) => void {
  const phases: Record<string, string> = {
    fetching_sas: "Getting upload URL...",
    uploading: "Uploading file...",
  };

  return (status: string): void => {
    const label = phases[status];
    if (label) {
      setUploadStatus(label);
      dispatch(updateUploadInTable({ uploadId, status: label }));
    }
  };
}

/** A message that clears itself five seconds after it is set. */
function useAutoClearedMessage(): [string, (message: string) => void] {
  const [message, setMessage] = useState<string>("");

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(""), FILE_TYPE_ERROR_TIMEOUT_MS);
      return () => clearTimeout(timer);
    }
  }, [message]);

  return [message, setMessage];
}

interface UploadContext {
  dispatch: AppDispatch;
  username: string;
  currentProjectId: string | null;
  setUploadStatus: (status: string) => void;
  setUploadError: (message: string) => void;
}

/** Push one file through the upload service, reporting progress as it goes. */
async function uploadOneFile(ctx: UploadContext, selectedFile: File): Promise<void> {
  const { dispatch, currentProjectId } = ctx;

  // Generate unique ID even when multiple uploads start simultaneously
  const uploadId = `upload-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

  // Initialize upload in table
  dispatch(
    updateUploadInTable({
      uploadId,
      fileName: selectedFile.name,
      fileSize: selectedFile.size,
      progress: 0,
      status: "Preparing upload...",
      completed: false,
      error: false,
    })
  );

  try {
    const { filePath } = await uploadFileToProject({
      file: selectedFile,
      username: ctx.username,
      projectId: currentProjectId,
      onStatusChange: statusReporter(dispatch, uploadId, ctx.setUploadStatus),
      onProgress: ({ percent }: { percent: number | null }) => {
        if (percent == null) return;
        dispatch(updateUploadInTable({ uploadId, progress: percent }));
      },
    });

    ctx.setUploadStatus("");

    if (currentProjectId && filePath) {
      dispatch(
        updateUploadInTable({
          uploadId,
          progress: 100,
          status: "Uploaded. Waiting for processing...",
          completed: false, // Don't mark as completed yet - wait for backend processing
        })
      );

      void dispatch(sendMessage(`CONTROL_FILE_UPLOAD:${currentProjectId}:${filePath}`));
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
    ctx.setUploadError(errorMessage);
    ctx.setUploadStatus("");
    dispatch(
      updateUploadInTable({
        uploadId,
        status: `Upload failed: ${errorMessage}`,
        error: true,
      })
    );
  }
}

/**
 * Owns the file-upload half of the chat input: validation, progress reporting
 * and the transient error banner.
 */
export function useFileUploads(): FileUploads {
  const dispatch = useAppDispatch();
  const currentProjectId = useAppSelector(selectCurrentProjectId);
  const reduxAuth = useAppSelector((state) => state.userAuthAndAdminSlice);

  const [uploadStatus, setUploadStatus] = useState<string>("");
  const [uploadError, setUploadError] = useState<string>("");
  const [uploadingFile, setUploadingFile] = useState<boolean>(false);
  const [fileTypeError, setFileTypeError] = useAutoClearedMessage();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetFileInput = (): void => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  /** Upload every selected file in parallel. */
  const runUploads = async (files: File[]): Promise<void> => {
    setUploadError("");
    setUploadStatus("Getting upload URL...");
    setUploadingFile(true);

    const ctx = {
      dispatch,
      username: getCurrentUserId(reduxAuth),
      currentProjectId,
      setUploadStatus,
      setUploadError,
    };

    try {
      await Promise.all(files.map((file) => uploadOneFile(ctx, file)));
    } finally {
      setUploadingFile(false);
      resetFileInput();
    }
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ): Promise<void> => {
    const selectedFiles = event.target.files;
    if (!selectedFiles || selectedFiles.length === 0) {
      return;
    }

    const files = Array.from(selectedFiles);
    const problem = validationError(files);
    if (problem) {
      setFileTypeError(problem);
      resetFileInput();
      return;
    }

    // Clear any previous error
    setFileTypeError("");
    await runUploads(files);
  };

  return {
    uploadStatus,
    uploadError,
    uploadingFile,
    fileTypeError,
    fileInputRef,
    clearFileTypeError: () => setFileTypeError(""),
    openFilePicker: () => fileInputRef.current?.click(),
    handleFileChange,
  };
}
